import apiClient from '../services/apiClient';
import { DEFAULT_OFF_MODULE_KEYS, PATH_MODULE_RULES } from '../constants/clientModules';
import AuthService from '../../modules/Admin/services/AuthService';
import { fallbackAllowedCodesForIndustry } from './industryModules';
import { fetchTenantIndustry } from '../services/TenantService';
import { Roles } from '../../routes/roles';


export const MODULE_ACCESS_STORAGE_KEY = 'nf_client_module_access_v2';

/** Matches backend ClientModuleAccessHelper.DefaultOffKeys — OFF until Super Admin enables. */
const DEFAULT_OFF_MODULES = DEFAULT_OFF_MODULE_KEYS;

let inflight = null;

function storedRoleId() {
  const r = AuthService.getLoggedInRoleId();
  return r != null ? String(r).toUpperCase() : '';
}

function storedClientId() {
  const c = AuthService.getLoggedInClientId();
  return c != null && c !== '' ? String(c) : '';
}

function storedAccessToken() {
  return AuthService.getToken();
}

/** Default when module map is missing or key absent — aligned with backend defaults. */
function defaultModuleEnabled(moduleKey) {
  return !DEFAULT_OFF_MODULES.has(moduleKey);
}

function resolveModuleFlag(map, moduleKey) {
  if (map && typeof map[moduleKey] === 'boolean') return map[moduleKey];
  return defaultModuleEnabled(moduleKey);
}

export function clearModuleAccessCache() {
  try {
    sessionStorage.removeItem(MODULE_ACCESS_STORAGE_KEY);
    sessionStorage.removeItem('nf_client_module_access_v1');
  } catch {
    /* ignore */
  }
  inflight = null;
}

function readTenantIndustryId() {
  try {
    const raw = sessionStorage.getItem('nf_tenant_industry_v2');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parsed?.agencyCategoryId;
    return id != null && !Number.isNaN(Number(id)) ? Number(id) : null;
  } catch {
    return null;
  }
}

/** Defense-in-depth: never expose another industry's modules in UI even if cache is stale. */
function isModuleAllowedForTenantIndustry(moduleKey) {
  const industryId = readTenantIndustryId();
  if (industryId == null) return true;
  return fallbackAllowedCodesForIndustry(industryId).has(moduleKey);
}

/** Fetch module map for logged-in tenant and cache in sessionStorage. */
export async function prefetchTenantModuleAccess() {
  const role = storedRoleId();
  // SuperAdmin doesn't need module access check
  if (role === Roles.SuperAdmin.toUpperCase() || !role) {
    clearModuleAccessCache();
    return;
  }
  const cid = storedClientId();
  if (!cid) return;

  const token = storedAccessToken();
  const [res] = await Promise.all([
    apiClient.get(`/Client/GetModuleAccess/${cid}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),
    fetchTenantIndustry(cid).catch(() => null),
  ]);
  sessionStorage.setItem(MODULE_ACCESS_STORAGE_KEY, JSON.stringify(res.data ?? {}));
}

export function ensureModuleAccessLoaded() {
  const role = storedRoleId();
  if (role === Roles.SuperAdmin.toUpperCase() || !storedClientId()) return Promise.resolve();
  if (sessionStorage.getItem(MODULE_ACCESS_STORAGE_KEY)) return Promise.resolve();
  if (!inflight) {
    inflight = prefetchTenantModuleAccess()
      .catch(() => {})
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function readModuleAccessMap() {
  try {
    const raw = sessionStorage.getItem(MODULE_ACCESS_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    return typeof o === 'object' && o !== null ? o : null;
  } catch {
    return null;
  }
}

export function hasModuleAccessLoaded() {
  const role = storedRoleId();
  if (role === Roles.SuperAdmin.toUpperCase() || !role) return true;
  return !!readModuleAccessMap();
}

export function isModuleEnabled(moduleKey) {
  const role = storedRoleId();
  if (role === Roles.SuperAdmin.toUpperCase()) return true;
  if (!isModuleAllowedForTenantIndustry(moduleKey)) return false;
  return resolveModuleFlag(readModuleAccessMap(), moduleKey);
}

export function moduleKeyForPath(pathname) {
  const path = pathname || '';
  for (const [re, key] of PATH_MODULE_RULES) {
    if (re.test(path)) return key;
  }
  return null;
}

export function isPathModuleAllowed(pathname) {
  const role = storedRoleId();
  if (role === Roles.SuperAdmin.toUpperCase()) return true;
  const path = pathname || '';
  if (/^\/partner(?:\/|$)/i.test(path)) return true;
  // Channel partner roles can only access partner routes
  if (role === Roles.ChannelPartnerAdmin.toUpperCase() || role === Roles.ChannelPartnerUser.toUpperCase()) {
    return /^\/partner(?:\/|$)/i.test(path) || path === '/unauthorized';
  }
  if (/^\/addlead(?:\/|$)/i.test(path)) {
    return isModuleEnabled('leads') || isModuleEnabled('assignedLeads');
  }
  const key = moduleKeyForPath(pathname);
  if (!key) return true;
  return isModuleEnabled(key);
}
